import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { environment } from '../../environments/environment';
import { ChatMessage } from '../models/chat-message.model';

@Injectable({
    providedIn: 'root'
})
export class ChatService {
    private stompClient: Client | null = null;
    private messageQueue: Array<{ destination: string, body: string }> = [];
    private messageSubject: Subject<ChatMessage> = new Subject<ChatMessage>();
    public messages$: Observable<ChatMessage> = this.messageSubject.asObservable();

    private unreadCountSubject: Subject<UnreadCountUpdate> = new Subject<UnreadCountUpdate>();
    public unreadCount$: Observable<UnreadCountUpdate> = this.unreadCountSubject.asObservable();

    private chatOpenSubject: Subject<boolean> = new Subject<boolean>();
    public chatOpen$: Observable<boolean> = this.chatOpenSubject.asObservable();

    private closePopupSubject: Subject<void> = new Subject<void>();
    public closePopup$: Observable<void> = this.closePopupSubject.asObservable();

    private soundNotificationSubject: Subject<void> = new Subject<void>();
    public soundNotification$: Observable<void> = this.soundNotificationSubject.asObservable();

    private chatActiveSubject: Subject<boolean> = new Subject<boolean>();
    public chatActive$: Observable<boolean> = this.chatActiveSubject.asObservable();

    private connected = false;

    private _isChatActive: boolean = true;
    private currentUserId: number | null = null;
    private previousUnreadCounts: number | null ;

    constructor(private http: HttpClient) {
        this._isChatActive = true;
        this.chatActiveSubject.next(this._isChatActive);
    }

    /**
     * Set current user ID for filtering sounds
     */
    setCurrentUserId(userId: number): void {
        this.currentUserId = userId;
    }

    /**
     * Get chat active status
     */
    get isChatActive(): boolean {
        return this._isChatActive;
    }

    /**
     * Set chat active status
     */
    set isChatActive(value: boolean) {
        this._isChatActive = value;
        this.chatActiveSubject.next(value);
    }

    /**
     * Connect to WebSocket
     */
    connect(candidatId: number, agencyId: number): void {
        if (this.connected) {
            return;
        }

        this.stompClient = new Client({
            webSocketFactory: () => new SockJS(`${environment.api.BASE_URL_NOTIF}/ws`),
            debug: () => { }, // Disable debug logging
            reconnectDelay: 5000,
            splitLargeFrames: true, // Enable splitting large frames for file attachments
            forceBinaryWSFrames: false, // SockJS doesn't support binary frames
            onConnect: () => {
                this.connected = true;

                // Flush any queued messages
                this.flushMessageQueue();

                // Subscribe to the conversation topic
                const topic = `/topic/chat/${candidatId}/${agencyId}`;
                this.stompClient!.subscribe(topic, (message) => {
                    const chatMessage: ChatMessage = JSON.parse(message.body);
                    chatMessage.timestamp = new Date(chatMessage.timestamp);
                    
                    // Emit sound notification for received messages (not from current user)
                    if (chatMessage.senderId !== this.currentUserId) {
                        this.soundNotificationSubject.next();
                    }
                    
                    this.messageSubject.next(chatMessage);
                });

                // Subscribe to unread count updates
                const unreadTopic = `/topic/unread/${candidatId}/${agencyId}`;
                this.stompClient!.subscribe(unreadTopic, (message) => {
                    const unreadUpdate = JSON.parse(message.body);
                    this.unreadCountSubject.next(unreadUpdate);
                });
            },
            onStompError: (frame) => {
                console.error('WebSocket connection error:', frame);
                this.connected = false;
            }
        });

        this.stompClient.activate();
    }

    /**
     * Disconnect from WebSocket
     */
    disconnect(): void {
        if (this.stompClient && this.connected) {
            this.stompClient.deactivate();
            this.connected = false;
        }
    }

    /**
     * Send a message via WebSocket
     */
    sendMessage(message: ChatMessage): void {
        const payload = {
            destination: '/app/chat.send',
            body: JSON.stringify(message)
        };

        // Emit sound notification for sent messages
        this.soundNotificationSubject.next();

        if (this.stompClient && this.connected) {
            this.stompClient.publish(payload);
        } else {
            // Queue the message to be sent when connected
            this.messageQueue.push(payload);
        }
    }

    /**
     * Notify server that conversation is opened - triggers auto-read
     */
    notifyConversationOpen(candidatId: number, agencyId: number, userId: number, senderType: string): void {
        const openDto = {
            candidatId: candidatId,
            agencyId: agencyId,
            userId: userId,
            senderType: senderType
        };
        const payload = {
            destination: '/app/chat.open',
            body: JSON.stringify(openDto)
        };

        if (this.stompClient && this.connected) {
            this.stompClient.publish(payload);
        } else {
            // Queue to be sent when connected
            this.messageQueue.push(payload);
        }
    }

    /**
     * Flush queued messages when connection is established
     */
    private flushMessageQueue(): void {
        if (this.stompClient && this.connected) {
            while (this.messageQueue.length > 0) {
                const payload = this.messageQueue.shift();
                if (payload) {
                    this.stompClient.publish(payload);
                }
            }
        }
    }

    /**
     * Get conversation history from REST API
     */
    getConversationHistory(candidatId: number, agencyId: number): Observable<ChatMessage[]> {
        return this.http.get<ChatMessage[]>(
            `${environment.api.BASE_URL}/chat/history?candidatId=${candidatId}&agencyId=${agencyId}`
        );
    }

    /**
     * Mark messages as read
     */
    markMessagesAsRead(candidatId: number, agencyId: number, userId: number): Observable<void> {
        this.previousUnreadCounts = 0 ; // Reset previous count to avoid sound on next update
        return this.http.put<void>(
            `${environment.api.BASE_URL}/chat/mark-read?candidatId=${candidatId}&agencyId=${agencyId}&userId=${userId}`,
            {}
        );
    }

    /**
     * Get unread message count
     */
    getUnreadCount(candidatId: number, agencyId: number, userId: number): Observable<number> {
        return this.http.get<number>(
            `${environment.api.BASE_URL}/chat/unread-count?candidatId=${candidatId}&agencyId=${agencyId}&userId=${userId}`
        );
    }

    /**
     * Get total unread count for user across all conversations with sound notification on increase
     */
    getTotalUnreadCount(userId: number, userType: string): Observable<number> {
        return new Observable(observer => {
            this._getTotalUnreadCount(userId, userType).subscribe(count => {
                const previousCount = this.previousUnreadCounts;
                
                // Emit sound if count increased
                if (previousCount != null && count > previousCount) {
                    this.playMessageSound();
                }
                
                this.previousUnreadCounts = count || 0;
                observer.next(count);
            });
        });
    }

    /**
     * Get total unread count for user across all conversations
     */
    _getTotalUnreadCount(userId: number, userType: string): Observable<number> {
        return this.http.get<number>(
            `${environment.api.BASE_URL}/chat/unread-total/${userId}?userType=${userType}`
        );
    }

    /**
     * Get unread count by agencies
     */
    getUnreadByAgency(candidatId: number): Observable<any[]> {
        return this.http.get<any[]>(
            `${environment.api.BASE_URL}/chat/unread-by-agency/${candidatId}`
        );
    }

    /**
     * Notify that chat is opened
     */
    notifyChatOpen(): void {
        this.chatOpenSubject.next(true);
    }

    /**
     * Notify that chat is closed
     */
    notifyChatClose(): void {
        this.chatOpenSubject.next(false);
    }

    /**
     * Request to close the chat popup
     */
    closeChatPopup(): void {
        this.closePopupSubject.next();
    }

    playMessageSound(): void {
        // Create a simple beep sound using Web Audio API
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        // Set sound properties: frequency 800Hz, short duration
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';

        // Create fade in and out effect
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
    }
}

export interface UnreadCountUpdate {
    candidatId: number;
    agencyId: number;
    unreadCount: number;
}
